import { useMemo, useState } from "react";
import clsx from "clsx";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import {
  ALERT_PARAMETER_LIST,
  type AlertConfig,
  type AlertDirection,
  type AlertParameterKey,
  type PumpId,
} from "@tbm/shared";

interface DrawerProps {
  open: boolean;
  onClose: () => void;
}

export function AlertsDrawer({ open, onClose }: DrawerProps) {
  const qc = useQueryClient();
  const configs = useQuery({
    queryKey: ["alerts-configs"],
    queryFn: api.alerts.configs,
    enabled: open,
  });
  const recipients = useQuery({
    queryKey: ["alerts-recipients"],
    queryFn: api.alerts.recipients,
    enabled: open,
  });
  const events = useQuery({
    queryKey: ["alerts-events"],
    queryFn: api.alerts.events,
    enabled: open,
    refetchInterval: open ? 15_000 : undefined,
  });
  const status = useQuery({
    queryKey: ["alerts-status"],
    queryFn: api.alerts.status,
    enabled: open,
    refetchInterval: open ? 10_000 : undefined,
  });

  const invalidateAll = () => {
    qc.invalidateQueries({ queryKey: ["alerts-configs"] });
    qc.invalidateQueries({ queryKey: ["alerts-status"] });
    qc.invalidateQueries({ queryKey: ["alerts-events"] });
  };

  return (
    <>
      <div
        className={clsx(
          "fixed inset-0 bg-black/25 transition-opacity z-40",
          open ? "opacity-100" : "opacity-0 pointer-events-none",
        )}
        onClick={onClose}
      />
      <aside
        className={clsx(
          "fixed top-0 right-0 bottom-0 w-[520px] max-lg:w-full max-lg:max-w-none z-50 bg-surface1 border-l border-border1",
          "transform transition-transform duration-200",
          open ? "translate-x-0" : "translate-x-full",
        )}
      >
        <div className="flex items-center justify-between h-16 px-6 border-b border-border1">
          <div>
            <div className="text-[10.5px] uppercase tracking-[0.22em] text-dim">Tools</div>
            <h2 className="text-[15px] font-semibold text-text">Warnings</h2>
          </div>
          <button onClick={onClose} className="text-muted hover:text-text text-lg">&times;</button>
        </div>

        <div className="p-5 space-y-6 overflow-y-auto h-[calc(100%-64px)]">
          {!status.data?.emailConfigured && (
            <div className="text-[12px] text-warn border border-warn/30 rounded px-3 py-2">
              Email not configured — alerts will be recorded but no emails sent.
              Set RESEND_API_KEY and ALERT_FROM_EMAIL on the app service.
            </div>
          )}

          <section>
            <h3 className="text-[10.5px] uppercase tracking-[0.22em] text-dim mb-2">
              Recent events
            </h3>
            {events.data?.events.length === 0 && (
              <p className="text-[12px] text-muted">No alert events yet.</p>
            )}
            <ul className="space-y-1.5 max-h-[150px] overflow-y-auto">
              {(events.data?.events ?? []).slice(0, 50).map((e) => (
                <li
                  key={e.id}
                  className={clsx(
                    "border rounded px-3 py-1.5 text-[12px] flex items-baseline gap-2 flex-wrap",
                    e.resolvedAt ? "border-border1" : "border-wb/40",
                  )}
                >
                  <span className={e.resolvedAt ? "text-muted" : "text-wb"}>
                    {e.resolvedAt ? "Resolved" : "Active"}
                  </span>
                  <span className="font-medium text-text">{e.displayName}</span>
                  <span className="mono-nums text-muted">
                    {e.value.toFixed(1)} {e.unit}
                    {" "}({e.direction} {e.threshold} {e.unit})
                  </span>
                  <span className="mono-nums text-dim text-[11px] ml-auto">
                    {fmtTs(e.triggeredAt)}
                  </span>
                  {e.notified && (
                    <span className="text-[10px] text-ok uppercase tracking-wider">emailed</span>
                  )}
                </li>
              ))}
            </ul>
          </section>

          <section>
            <h3 className="text-[10.5px] uppercase tracking-[0.22em] text-dim mb-2">
              Active alerts ({configs.data?.configs.length ?? 0})
            </h3>
            {configs.data?.configs.length === 0 && (
              <p className="text-[12px] text-muted">No alerts configured yet.</p>
            )}
            <ul className="space-y-2">
              {(configs.data?.configs ?? []).map((c) => (
                <AlertConfigRow
                  key={c.id}
                  config={c}
                  status={status.data?.alerts.find((a) => a.configId === c.id)}
                  onMutate={invalidateAll}
                />
              ))}
            </ul>
          </section>

          <AddAlertForm onSuccess={invalidateAll} />

          <RecipientsSection
            recipients={recipients.data?.recipients ?? []}
          />
        </div>
      </aside>
    </>
  );
}

function AddAlertForm({ onSuccess }: { onSuccess: () => void }) {
  const [paramKey, setParamKey] = useState<AlertParameterKey>("jackingForce");
  const [direction, setDirection] = useState<AlertDirection>("above");
  const [threshold, setThreshold] = useState("");
  const [hysteresis, setHysteresis] = useState("10");

  const availability = useQuery({
    queryKey: ["pumps", "availability"],
    queryFn: api.pumpAvailability,
    staleTime: 60_000,
  });

  const availablePumps = useMemo(() => {
    const s = new Set<PumpId>();
    for (const p of availability.data?.pumps ?? []) {
      if (p.hasData) s.add(p.id);
    }
    return s;
  }, [availability.data]);

  const filteredParams = useMemo(
    () => ALERT_PARAMETER_LIST.filter((p) => !p.pumpId || availablePumps.has(p.pumpId)),
    [availablePumps],
  );

  const create = useMutation({
    mutationFn: () =>
      api.alerts.createConfig({
        parameterKey: paramKey,
        direction,
        threshold: Number(threshold),
        hysteresisPct: Number(hysteresis) || 10,
      }),
    onSuccess: () => {
      setThreshold("");
      onSuccess();
    },
  });

  const selectedParam = filteredParams.find((p) => p.key === paramKey);
  const canSubmit = threshold.length > 0 && Number.isFinite(Number(threshold)) && !create.isPending;

  return (
    <section className="space-y-3">
      <h3 className="text-[10.5px] uppercase tracking-[0.22em] text-dim">
        Add alert
      </h3>
      <div className="grid grid-cols-2 gap-2">
        <select
          value={paramKey}
          onChange={(e) => setParamKey(e.target.value as AlertParameterKey)}
          className="col-span-2 bg-surface2 border border-border1 rounded px-3 py-2 text-text text-[13px] focus:border-wb/60 outline-none"
        >
          {filteredParams.map((p) => (
            <option key={p.key} value={p.key}>
              {p.displayName}{p.unit ? ` (${p.unit})` : ""}
            </option>
          ))}
        </select>
        <select
          value={direction}
          onChange={(e) => setDirection(e.target.value as AlertDirection)}
          className="bg-surface2 border border-border1 rounded px-3 py-2 text-text text-[13px] focus:border-wb/60 outline-none"
        >
          <option value="above">Above</option>
          <option value="below">Below</option>
        </select>
        <input
          type="number"
          value={threshold}
          onChange={(e) => setThreshold(e.target.value)}
          placeholder={`Threshold (${selectedParam?.unit ?? ""})`}
          className="bg-surface2 border border-border1 rounded px-3 py-2 text-text text-[13px] mono-nums focus:border-wb/60 outline-none"
        />
      </div>
      <div className="flex items-center gap-2">
        <label className="flex items-center gap-1.5 text-[11px] text-muted">
          Dead band
          <input
            type="number"
            value={hysteresis}
            onChange={(e) => setHysteresis(e.target.value)}
            min="0"
            max="50"
            step="1"
            className="w-14 bg-surface2 border border-border1 rounded px-2 py-1 text-text text-[12px] mono-nums focus:border-wb/60 outline-none text-center"
          />
          %
        </label>
      </div>
      <div className="flex items-center gap-2">
        <button
          onClick={() => create.mutate()}
          disabled={!canSubmit}
          className={clsx(
            "rounded px-3 py-2 text-[11px] uppercase tracking-widest transition-colors",
            canSubmit
              ? "bg-wb hover:bg-wbHot text-white"
              : "bg-border1 text-dim cursor-not-allowed",
          )}
        >
          {create.isPending ? "Saving..." : "Add alert"}
        </button>
      </div>
      {create.isError && (
        <p className="text-[12px] text-wb">{(create.error as Error).message}</p>
      )}
    </section>
  );
}

function AlertConfigRow({
  config,
  status,
  onMutate,
}: {
  config: AlertConfig;
  status?: { breached: boolean; currentValue: number | null; lastBreachAt: string | null };
  onMutate: () => void;
}) {
  const toggle = useMutation({
    mutationFn: () => api.alerts.updateConfig(config.id, { enabled: !config.enabled }),
    onSuccess: onMutate,
  });
  const del = useMutation({
    mutationFn: () => api.alerts.deleteConfig(config.id),
    onSuccess: onMutate,
  });
  const breached = status?.breached ?? false;

  return (
    <li
      className={clsx(
        "border rounded px-3 py-2 flex items-center gap-3",
        breached ? "border-wb/50 bg-wb/5" : "border-border1",
      )}
    >
      <span
        className={clsx(
          "h-2.5 w-2.5 rounded-full flex-shrink-0",
          breached ? "bg-wb animate-pulseLive" : config.enabled ? "bg-ok" : "bg-dim",
        )}
      />
      <div className="flex-1 min-w-0">
        <div className="text-[13px] font-medium text-text truncate">
          {config.displayName}
          <span className="text-muted ml-1.5">
            {config.direction} {config.threshold} {config.unit}
          </span>
        </div>
        <div className="text-[11px] text-dim flex items-center gap-2">
          {status?.currentValue != null && (
            <span className={clsx("mono-nums", breached ? "text-wb" : "text-muted")}>
              now: {status.currentValue.toFixed(1)} {config.unit}
            </span>
          )}
          {status?.lastBreachAt && (
            <span className="mono-nums">
              last breach: {fmtTs(status.lastBreachAt)}
            </span>
          )}
          <span className="text-dim">
            {config.hysteresisPct}% dead band
          </span>
        </div>
      </div>
      <button
        onClick={() => toggle.mutate()}
        className="text-[10px] uppercase tracking-wider text-muted hover:text-text"
      >
        {config.enabled ? "Disable" : "Enable"}
      </button>
      <button
        onClick={() => del.mutate()}
        className="text-[10px] uppercase tracking-wider text-muted hover:text-wb"
      >
        Delete
      </button>
    </li>
  );
}

function RecipientsSection({
  recipients,
}: {
  recipients: Array<{ id: number; email: string }>;
}) {
  const qc = useQueryClient();
  const [email, setEmail] = useState("");

  const add = useMutation({
    mutationFn: () => api.alerts.addRecipient({ email }),
    onSuccess: () => {
      setEmail("");
      qc.invalidateQueries({ queryKey: ["alerts-recipients"] });
    },
  });
  const del = useMutation({
    mutationFn: (id: number) => api.alerts.deleteRecipient(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["alerts-recipients"] }),
  });

  const canAdd = email.includes("@") && !add.isPending;

  return (
    <section className="space-y-3">
      <h3 className="text-[10.5px] uppercase tracking-[0.22em] text-dim">
        Email recipients
      </h3>
      <div className="flex gap-2">
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="email@example.com"
          className="flex-1 bg-surface2 border border-border1 rounded px-3 py-2 text-text text-[13px] focus:border-wb/60 outline-none"
        />
        <button
          onClick={() => add.mutate()}
          disabled={!canAdd}
          className={clsx(
            "rounded px-3 py-2 text-[11px] uppercase tracking-widest transition-colors",
            canAdd
              ? "bg-wb hover:bg-wbHot text-white"
              : "bg-border1 text-dim cursor-not-allowed",
          )}
        >
          Add
        </button>
      </div>
      {add.isError && (
        <p className="text-[12px] text-wb">{(add.error as Error).message}</p>
      )}
      {recipients.length === 0 && (
        <p className="text-[12px] text-muted">No recipients added yet.</p>
      )}
      <ul className="space-y-1">
        {recipients.map((r) => (
          <li key={r.id} className="flex items-center gap-2 text-[12px]">
            <span className="text-text">{r.email}</span>
            <button
              onClick={() => del.mutate(r.id)}
              className="ml-auto text-[10.5px] text-muted hover:text-wb uppercase tracking-wider"
            >
              Remove
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}

function fmtTs(iso: string): string {
  const d = new Date(iso);
  const now = Date.now();
  const agoSec = Math.max(0, Math.round((now - d.getTime()) / 1000));
  if (agoSec < 60) return `${agoSec}s ago`;
  if (agoSec < 3600) return `${Math.round(agoSec / 60)}m ago`;
  if (agoSec < 86400) {
    const h = Math.floor(agoSec / 3600);
    return `${h}h ago`;
  }
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}
